/**
 * FotkyModal — the photos of one fleet car (per-row modal). They ARE the car's own gallery (the one its detail
 * shows: used or new car) — the rental keeps no copy. What the rental adds is its CHOICE: which of the car's photos
 * it shows and in which order (the first is the cover). At `pujcovna:write` a photo can be chosen / dropped,
 * made the cover, or added (it goes to the car's gallery and is chosen); deleting a photo is done on the car's
 * detail, where it belongs. Same fresh-read, freeze and no-dismiss-while-saving rules as the other fleet modals.
 */

import { useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Icon } from '@/components/Icon';
import { Modal, DialogCrown } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toaster';
import { useChooseFotkyMutation, useFotkyQuery, useUploadFotoMutation } from './api';

interface FotkyModalProps {
  /** The fleet entry id. */
  id: number;
  /** What the dialog titles the car with (plate or VIN). */
  title: string;
  /** The stock car whose gallery this is — where photos are deleted. */
  zdroj: { typ: 'nove' | 'ojete'; id: number };
  canWrite: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FotkyModal({ id, title, zdroj, canWrite, open, onOpenChange }: FotkyModalProps) {
  const query = useFotkyQuery(id);
  const upload = useUploadFotoMutation(id);
  const choose = useChooseFotkyMutation(id);
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const busy = progress != null || upload.isPending || choose.isPending;

  // Every way out ends in `onOpenChange(false)`; while a write is pending it would unmount the observer its
  // toasts hang on, so it waits.
  const guarded = (next: boolean) => {
    if (!next && busy) return;
    onOpenChange(next);
  };
  // The modal mounts per open: it renders only from a read that finished after the mount (a cached snapshot's
  // signed URLs may have expired), and a failed read shows no gallery.
  const fresh = query.isSuccess && query.isFetchedAfterMount;
  const data = query.data;
  const fotky = data?.fotky ?? [];
  const chosen = fotky.filter((f) => f.vybrano).map((f) => f.key);
  const carPath = zdroj.typ === 'nove' ? `/vozidla/${zdroj.id}` : `/ojeta-vozidla/${zdroj.id}`;

  const save = (keys: string[], okMessage: string) =>
    choose.mutate(keys, {
      onSuccess: () => toast.ok(okMessage),
      onError: (err) => toast.error(`Chyba: ${err instanceof Error ? err.message : 'Nepodařilo se uložit výběr fotografií.'}`),
    });

  async function uploadFiles(list: FileList | null) {
    if (!list || list.length === 0 || busy) return;
    const images = Array.from(list).filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) {
      toast.warn('Vyberte obrázek — JPG, PNG nebo GIF.');
      return;
    }
    let ok = 0;
    setProgress({ done: 0, total: images.length });
    try {
      for (const [i, file] of images.entries()) {
        try {
          await upload.mutateAsync(file);
          ok += 1;
        } catch (err) {
          toast.error(`${file.name}: ${err instanceof Error ? err.message : 'Nepodařilo se nahrát fotografii.'}`);
        }
        setProgress({ done: i + 1, total: images.length });
      }
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = '';
    }
    if (ok > 0) toast.ok(ok === 1 ? 'Fotografie přidána do galerie vozu.' : `Přidáno fotografií: ${ok}.`);
  }

  return (
    <Modal open={open} onOpenChange={guarded} maxWidth={960}>
      <DialogCrown
        eyebrow="Vozidlo půjčovny"
        title={
          <>
            Fotky <em>{title}</em>
          </>
        }
        sub="Fotografie jsou z galerie vozu ve skladu. Zaškrtněte ty, které má půjčovna ukazovat; první je hlavní."
      />
      <Modal.Body className="px-8 py-5">
        {query.isError ? (
          <p data-testid="fotky-error" className="text-[13px] text-ink-2">
            Fotografie se nepodařilo načíst. Zkuste to prosím znovu za chvíli.
          </p>
        ) : !fresh || !data ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Skeleton height={140} />
            <Skeleton height={140} />
            <Skeleton height={140} />
          </div>
        ) : data.duvod ? (
          <p data-testid="fotky-nepripojene" className="text-[13px] text-ink-2">
            Vozidlo už není propojené s aktuálním záznamem ve skladu, fotogalerii nelze zobrazit.
          </p>
        ) : (
          <div className="flex flex-col gap-5">
            {canWrite && (
              <div className="flex flex-wrap items-center gap-3 border border-dashed border-line px-4 py-3">
                <input
                  ref={inputRef}
                  data-testid="fotky-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/gif"
                  multiple
                  className="hidden"
                  onChange={(e) => void uploadFiles(e.currentTarget.files)}
                />
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<Icon name="upload" size={12} />}
                  onClick={() => inputRef.current?.click()}
                  disabled={busy}
                >
                  {progress ? `Nahrávám ${progress.done + 1} z ${progress.total}…` : 'Přidat fotografie'}
                </Button>
                <span className="font-mono text-[11px] italic text-ink-3">
                  Přidají se do galerie vozu (JPG, PNG, GIF · max 10 MB).
                </span>
              </div>
            )}

            {fotky.length === 0 ? (
              <p data-testid="fotky-prazdno" className="text-[13px] text-ink-2">
                Vůz zatím nemá žádné fotografie.
              </p>
            ) : (
              <div data-testid="fotky-seznam">
                <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                  <span>
                    Galerie vozu · {fotky.length} · v půjčovně {chosen.length}
                  </span>
                  <Link to={carPath} className="normal-case tracking-normal text-accent-text hover:underline">
                    Mazání fotografií: detail vozu
                  </Link>
                </div>
                <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {fotky.map((f) => (
                    <li
                      key={f.key}
                      data-testid={`foto-${f.key}`}
                      data-vybrano={f.vybrano ? '1' : '0'}
                      data-poradi={f.poradi ?? ''}
                      className="flex flex-col border border-line bg-bg"
                    >
                      <div className="relative">
                        <a href={f.url} target="_blank" rel="noreferrer">
                          <img src={f.url} alt="Fotografie vozidla" loading="lazy" className="block aspect-[4/3] w-full object-cover" />
                        </a>
                        {f.poradi === 0 && (
                          <span className="absolute left-2 top-2">
                            <Badge tone="accent" size="sm">
                              Hlavní
                            </Badge>
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                        <Checkbox
                          label="V půjčovně"
                          checked={f.vybrano}
                          disabled={!canWrite || busy}
                          onChange={(e) =>
                            save(
                              e.currentTarget.checked ? [...chosen, f.key] : chosen.filter((k) => k !== f.key),
                              e.currentTarget.checked ? 'Fotografie přidána do půjčovny.' : 'Fotografie odebrána z půjčovny.',
                            )
                          }
                        />
                        {canWrite && f.vybrano && f.poradi !== 0 && (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={busy}
                            onClick={() => save([f.key, ...chosen.filter((k) => k !== f.key)], 'Hlavní fotografie nastavena.')}
                          >
                            Hlavní
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Foot
        actions={
          <Button variant="secondary" onClick={() => guarded(false)} disabled={busy}>
            Zavřít
          </Button>
        }
      />
    </Modal>
  );
}
